import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/constants';
import {
  Trophy, Flag, Timer, MapPin, Wrench, Users,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus
} from 'lucide-react';

const TEAM_COLORS = {
  'Mercedes-AMG Petronas': '#00D2BE',
  'Scuderia Ferrari': '#DC0000',
  'McLaren Racing': '#FF8700',
  'Red Bull Racing': '#3671C6',
  'Haas F1 Team': '#B6BABD',
  'Racing Bulls': '#6692FF',
  'Aston Martin': '#006F62',
  'Williams Racing': '#005AFF',
  'Alpine F1 Team': '#0090FF',
  'Stake F1 Team': '#52E252',
  'Sauber': '#52E252',
};

function getTeamColor(name) {
  for (const [key, color] of Object.entries(TEAM_COLORS)) {
    if (name?.includes(key) || key.includes(name)) return color;
  }
  return '#6b8299';
}

function TrendIcon({ history }) {
  if (!history || history.length < 2) return <Minus className="h-3.5 w-3.5 text-white/30" />;
  const latest = history[history.length - 1]?.position;
  const prev = history[history.length - 2]?.position;
  if (latest < prev) return <TrendingUp className="h-3.5 w-3.5 text-[#2A9D8F]" />;
  if (latest > prev) return <TrendingDown className="h-3.5 w-3.5 text-[#E74C3C]" />;
  return <Minus className="h-3.5 w-3.5 text-white/30" />;
}

function PositionBar({ history, color }) {
  if (!history?.length) return null;
  const maxPos = 10;
  return (
    <div className="flex items-end gap-1.5" style={{ height: 48 }}>
      {history.map((h) => {
        const pct = Math.max(10, ((maxPos - h.position + 1) / maxPos) * 100);
        return (
          <div key={h.season} className="flex flex-col items-center gap-1">
            <span className="text-[9px] font-mono font-bold text-white/70">P{h.position}</span>
            <div
              className="w-6 rounded-t-sm transition-all"
              style={{ height: `${pct * 0.4}px`, backgroundColor: color, opacity: 0.7 + (h.season - 2022) * 0.1 }}
            />
            <span className="text-[8px] text-white/30">{String(h.season).slice(2)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ConstructorCard({ team }) {
  const [expanded, setExpanded] = useState(false);
  const { current, profile, history } = team;
  const color = getTeamColor(current?.name);
  const pos = current?.position;

  return (
    <div
      className="overflow-hidden rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)] transition-colors hover:border-white/20"
      style={{ borderTopWidth: '3px', borderTopColor: color }}
      data-testid={`f1-constructor-card-${current?.team_id || pos}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-4 p-5 text-left"
        data-testid={`f1-constructor-toggle-${current?.team_id || pos}`}
      >
        <div className="relative flex-shrink-0">
          <div className="absolute -left-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
            {pos}
          </div>
          {current?.team_logo ? (
            <img src={current.team_logo} alt={current.name} className="h-14 w-14 rounded-xl border border-white/10 bg-white/5 object-contain p-1" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/5">
              <Flag className="h-6 w-6 text-white/30" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-white">{current?.name}</h3>
          {profile?.base ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-white/40">
              <MapPin className="h-3 w-3" /> {profile.base}
            </p>
          ) : null}
        </div>

        <div className="hidden items-center gap-6 sm:flex">
          <div className="text-center">
            <p className="font-mono text-xl font-bold" style={{ color }}>{current?.points || 0}</p>
            <p className="text-[9px] uppercase tracking-wider text-white/30">Points</p>
          </div>
          {profile?.world_championships != null ? (
            <div className="text-center">
              <p className="font-mono text-xl font-bold text-[#D8B45A]">{profile.world_championships}</p>
              <p className="text-[9px] uppercase tracking-wider text-white/30">Titles</p>
            </div>
          ) : null}
          <div className="hidden lg:block">
            <PositionBar history={history} color={color} />
          </div>
          <TrendIcon history={history} />
        </div>

        {expanded ? <ChevronUp className="h-5 w-5 flex-shrink-0 text-white/40" /> : <ChevronDown className="h-5 w-5 flex-shrink-0 text-white/40" />}
      </button>

      {expanded ? (
        <div className="border-t border-white/5 px-5 pb-5">
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {profile?.world_championships != null ? (
              <StatBox icon={Trophy} label="World Titles" value={profile.world_championships} color="#D8B45A" />
            ) : null}
            {profile?.pole_positions != null ? (
              <StatBox icon={Flag} label="Pole Positions" value={profile.pole_positions} color="#9B59B6" />
            ) : null}
            {profile?.fastest_laps != null ? (
              <StatBox icon={Timer} label="Fastest Laps" value={profile.fastest_laps} color="#E67E22" />
            ) : null}
            {profile?.highest_race_finish ? (
              <StatBox icon={Trophy} label="Race Wins" value={profile.highest_race_finish.number || 0} color="#2A9D8F" />
            ) : null}
            {profile?.first_team_entry ? (
              <StatBox icon={Flag} label="First Entry" value={profile.first_team_entry} color="#3498DB" />
            ) : null}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {profile ? (
              <div className="rounded-xl border border-white/10 bg-[#0B1E31] p-4">
                <p className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/35">
                  <Users className="h-3 w-3" /> Team details
                </p>
                <div className="space-y-2">
                  {profile.director ? <DetailRow label="Team Principal" value={profile.director} /> : null}
                  {profile.president ? <DetailRow label="President" value={profile.president} /> : null}
                  {profile.technical_manager ? <DetailRow label="Technical Director" value={profile.technical_manager} /> : null}
                  {profile.chassis ? <DetailRow label="Chassis" value={profile.chassis} /> : null}
                  {profile.engine ? <DetailRow label="Power Unit" value={profile.engine} /> : null}
                  {profile.tyres ? <DetailRow label="Tyres" value={profile.tyres} /> : null}
                </div>
              </div>
            ) : null}

            {history?.length ? (
              <div className="rounded-xl border border-white/10 bg-[#0B1E31] p-4">
                <p className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/35">
                  <Wrench className="h-3 w-3" /> Season history
                </p>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.season} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="text-sm font-semibold text-white">{h.season}</span>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="font-mono text-sm font-bold" style={{ color }}>P{h.position}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-white/60">{h.points} pts</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2">
                    <span className="text-sm font-bold text-white">2026</span>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-sm font-bold" style={{ color }}>P{current?.position}</span>
                      <span className="text-sm text-white/60">{current?.points} pts</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B1E31] p-3 text-center">
      <Icon className="mx-auto h-4 w-4 mb-1" style={{ color }} />
      <p className="font-mono text-lg font-bold text-white">{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-white/35">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/40">{label}</span>
      <span className="font-medium text-white">{value}</span>
    </div>
  );
}

export default function F1ConstructorProfiles({ refreshTick }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/f1/team-benchmarks`)
      .then((res) => res.json())
      .then((data) => {
        setTeams(data.teams || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [refreshTick]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="f1-constructors-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-[#3498DB]" />
      </div>
    );
  }

  if (!teams.length) {
    return <p className="py-8 text-center text-sm text-white/40" data-testid="f1-constructors-empty">No constructor data available.</p>;
  }

  return (
    <div data-testid="f1-constructor-profiles-section">
      <div className="mb-5 flex items-center gap-3">
        <Flag className="h-5 w-5 text-[#E74C3C]" />
        <div>
          <h2 className="text-xl font-bold text-white">F1 Constructor Profiles</h2>
          <p className="text-xs text-white/40">2026 championship standings with team bios and historical performance</p>
        </div>
        <Badge className="ml-auto border border-white/10 bg-white/5 text-[10px] text-white/50">
          {teams.length} teams
        </Badge>
      </div>

      <div className="space-y-3">
        {teams.map((team) => (
          <ConstructorCard key={team.current?.team_id || team.current?.name} team={team} />
        ))}
      </div>
    </div>
  );
}
